import { useLoaderData, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Head } from 'vite-react-ssg';

import { getFooter, type FooterType } from '@/services/footer/footerService';
import { getHeader, type HeaderType } from '@/services/header/headerService';

import Header from '@/components/organisms/Header/Header';
import Footer from '@/components/organisms/Footer/Footer';
import Heading from '@/components/atoms/Heading/Heading';
import Button from '@/components/atoms/Button/Button';

import { getLocale, buildLocalizedPath } from '@/utils/localeSlug';

import PreTitle from '@/components/atoms/PreTitle/PreTitle';
import styles from './NotFound.module.css';

/** Même contrat que contactLoader / errorLoader */
export async function notFoundLoader({ request }: { request: Request }) {
  const url = new URL(request.url);
  const locale = getLocale(url);

  const [header, footer] = await Promise.all([
    getHeader(locale),
    getFooter(locale),
  ]);

  return { header, footer, locale };
}

type NotFoundData = Awaited<ReturnType<typeof notFoundLoader>>;
type StaticLoaderData = Record<string, Record<string, unknown> | undefined>;
type StaticRouterHydration = { loaderData?:Record<string, unknown> };

/**
 * En SSG, les loaders côté client lisent le manifeste à l'URL exacte. Une URL inconnue
 * (route `*`) n'y figure pas : on reprend les données de la page 404 générée pour la locale.
 */
function getPrerenderedNotFoundData(locale: ReturnType<typeof getLocale>): NotFoundData | null {
  if (typeof window === 'undefined') return null;

  const SSG_LOADER_DATA_KEY = '__VITE_REACT_SSG_STATIC_LOADER_DATA__';
  const manifest = (window as Window & Record<typeof SSG_LOADER_DATA_KEY, StaticLoaderData | undefined>)[
    SSG_LOADER_DATA_KEY
  ];
  const routesData = manifest?.[buildLocalizedPath('404', locale)];
  const data = routesData && Object.values(routesData).find(
    (routeData) => routeData && typeof routeData === 'object' && 'header' in routeData,
  );

  return (data as NotFoundData | undefined) ?? null;
}

function getEmbeddedNotFoundData(): NotFoundData | null {
  if (typeof window === 'undefined') return null;

  const ROUTER_HYDRATION_KEY = '__staticRouterHydrationData';
  const hydration = (window as Window & Record<typeof ROUTER_HYDRATION_KEY, StaticRouterHydration | undefined>)[
    ROUTER_HYDRATION_KEY
  ];

  const match = Object.values(hydration?.loaderData ?? {}).find(
    (value): value is NotFoundData =>
      value !== null
      && typeof value === 'object'
      && 'header' in value
      && 'footer' in value
      && 'locale' in value,
  );

  return match ?? null;
}

function NotFound() {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const locale = getLocale(pathname);
  const loaderData = useLoaderData() as NotFoundData | null;
  const data = loaderData ?? getEmbeddedNotFoundData() ?? getPrerenderedNotFoundData(locale);

  if (!data) return null;

  const { header, footer } = data;

  return (
    <main>
      <Head htmlAttributes={{ lang: locale }}>
        <title>{t('notFound.metaTitle')}</title>
        <meta name="description" content={t('notFound.metaDescription')} />
        <meta name="robots" content="noindex, nofollow" />
      </Head>

      <Header color="dark" header={header as HeaderType} localizations={[]} />

      <div className={styles.body}>
        <div className={styles.container}>
          <section className={styles.content}>
            <PreTitle preTitle={t('notFound.preTitle')} color="dark" />
            <Heading
              title={t('notFound.title')}
              Tag="h1"
              size="h1"
              color="dark"
              align="center"
            />
            <p className={styles.description}>{t('notFound.description')}</p>
            <Button title={t('notFound.goHome')} bgColor="red" url={buildLocalizedPath('', locale)} />
          </section>
        </div>
      </div>

      <Footer footer={footer as FooterType} />
    </main>
  );
}

export default NotFound;
